import {
    FieldMapping,
    CalculatedField,
    FieldCopy
} from '../../legacy/sb-form-configuration/sb-form-context';
import { get, set } from 'dot-prop';
import { evaluate } from 'mathjs';
import uuidv4 from 'uuid/v4';
import { getDateWithDayOffset } from './date-utils';
import { UntypedFormGroup } from '@angular/forms';

/** @deprecated - This function has been deprecated and
 * should not be used when developing new features. */
function setObject(name, value, context) {
    const parts = name.split('.');
    const key = parts.pop();
    for (let i = 0, j; context && (j = parts[i]); i++) {
        context = j in context ? context[j] : (context[j] = {});
    }
    return context && key ? (context[key] = value) : undefined;
}

function getObjectByKeyValue(key, value, theObject) {
    let result = null;
    if (theObject instanceof Array) {
        for (let i = 0; i < theObject.length; i++) {
            result = getObjectByKeyValue(key, value, theObject[i]);
            if (result) {
                break;
            }
        }
    } else {
        for (const prop of Object.keys(theObject)) {
            if (prop === key && theObject[prop] === value) {
                return theObject;
            }
            if (theObject[prop] instanceof Object || theObject[prop] instanceof Array) {
                result = getObjectByKeyValue(key, value, theObject[prop]);
                if (result) {
                    break;
                }
            }
        }
    }
    return result;
}

function getObjectDotPath(path, theObject) {
    const result = path.split('.').reduce((acc, value) => {
        if (acc != null) {
            return getObjectByKeyValue('key', value, acc);
        } else {
            return null;
        }
    }, theObject);
    return result;
}

function getObject(key, theObject) {
    let result = null;
    if (theObject instanceof Array) {
        for (let i = 0; i < theObject.length; i++) {
            result = getObject(key, theObject[i]);
            if (result) {
                break;
            }
        }
    } else {
        for (const prop of Object.keys(theObject)) {
            if (prop === key) {
                return theObject[key];
            }
            if (theObject[prop] instanceof Object || theObject[prop] instanceof Array) {
                result = getObject(key, theObject[prop]);
                if (result) {
                    break;
                }
            }
        }
    }
    return result;
}

function deepCopy(obj) {
    let copy;

    // Handle the 3 simple types, and null or undefined
    if (null == obj || 'object' !== typeof obj) {
        return obj;
    }

    // Handle Date
    if (obj instanceof Date) {
        copy = new Date();
        copy.setTime(obj.getTime());
        return copy;
    }

    // Handle Array
    if (obj instanceof Array) {
        copy = [];
        for (let i = 0, len = obj.length; i < len; i++) {
            copy[i] = deepCopy(obj[i]);
        }
        return copy;
    }

    // Handle Object
    if (obj instanceof Object) {
        copy = {};
        for (const attr in obj) {
            if (obj.hasOwnProperty(attr)) {
                copy[attr] = deepCopy(obj[attr]);
            }
        }
        return copy;
    }

    throw new Error('Unable to copy obj! Its type is not supported.');
}

/**
 * Expects an optional source model (typically read from db)
 *
 */
function transform(fieldMappingTuples: any[], from: boolean = true, sourceModel?: any) {
    fieldMappingTuples.reduce((acc, tuple) => {
        const [sourcePath, targetPath] = from ? tuple : tuple.reverse();
        const sourceValue = get(sourceModel, sourcePath);
        return set(acc, targetPath, sourceValue);
    }, {});
}

function transformModel(model, reverse: boolean = false, fieldMappings?: FieldMapping[]) {
    if (fieldMappings) {
        const transformedResult = {};
        const constantValues = fieldMappings.filter((fm) => fm.source.startsWith('#'));
        const standardFieldMappings = fieldMappings.filter(
            (fm) => !fm.source.startsWith('#')
        );
        constantValues.forEach((fm) => {
            const sourceValue = fm.source.substring(1);
            setObject(fm.target, sourceValue, transformedResult);
        });

        standardFieldMappings.forEach((fm) => {
            const [sourcePath, targetPath] = !reverse
                ? [fm.source, fm.target]
                : [fm.target, fm.source];
            const sourceValue = sourcePath.split('.').reduce((o, i) => {
                return o ? o[i] : null;
            }, model);
            if (sourceValue && fm.valueLabel && !reverse) {
                if (sourceValue.hasOwnProperty('value')) {
                    setObject(targetPath, sourceValue.value, transformedResult);
                } else {
                    setObject(targetPath, sourceValue, transformedResult);
                }
            } else {
                setObject(targetPath, sourceValue, transformedResult);
            }
        });
        return transformedResult;
    } else {
        return model;
    }
}

function parseDate(maybeDate) {
    if (maybeDate && typeof maybeDate.toISOString !== 'undefined') {
        return maybeDate.toISOString().substring(0, 10);
    }
    return maybeDate;
}

/**
 * The copy field configuration will take a source field value and copy it to a
 * target field value if it is blank.
 * e.g, Selection of a depository account will automatically set the payment account.
 */
function updateCopyFields(model, copyConfig: FieldCopy[], form: UntypedFormGroup) {
    const _model = copyConfig.reduce((acc, config) => {
        let source = get(acc, config.source);
        const target = get(acc, config.target) as string;

        const shouldCopy = !target || config.overwriteIfSet;
        if (!source || !shouldCopy) {
            return acc;
        }

        let touched = getFormControl(form, config.target)?.touched;
        if (touched && !config.overwriteIfTouched) {
            return acc;
        }

        if (config.dateOffset) {
            source = getDateWithDayOffset(
                source as Date,
                config.dateOffset.offset,
                config.dateOffset.includeWeekends
            );
        }

        set(acc, config.target, source);
        return { ...acc };
    }, model);
    return _model;
}

/** @deprecated - This function has been deprecated and should
 * not be used when developing new features. There's no need to
 * have a function handling UntypedFormGroups, our aim should be
 * to write type safe code and use typed forms. */
function getFormControl(form: UntypedFormGroup, target: string) {
    let control: any = form;
    for (let step of target.split('.')) {
        control = control?.controls[step];
    }
    return control;
}

function calculateFields(model, calculatedFields: CalculatedField[]) {
    if (!calculatedFields) {
        return model;
    }
    const _model = calculatedFields.reduce((acc, config) => {
        let scope = {};
        Object.keys(config.fields).forEach((key) => {
            const path = config.fields[key];
            let value = get(acc, path) || 0;
            if (typeof value === 'string') {
                value = value.replace(/\s/g, '');
            }
            scope = { ...scope, [key]: value };
        });
        try {
            const result = evaluate(config.mathExpression, scope);
            if (result) set(acc, config.target, result);
            return { ...acc };
        } catch (error) {
            console.error(error);
            return { ...acc };
        }
    }, model);
    return _model;
}

function getFormComponentItemtext(item, templateFields: string[]) {
    if (!item) {
        return '';
    }
    if (item.hasOwnProperty('value') && item.hasOwnProperty('label')) {
        // Standard dropdown or multiselect
        return item.label;
    }
    if (templateFields) {
        // Dropdown or multiselect with predefined template fields.
        return templateFields.map((f) => item[f]).join(' ');
    }
    return item;
}

function getMultiSelectDisplayText(options, formControl) {
    let displayText = '';
    let selectedOptions = [];
    if (formControl && formControl.value) {
        selectedOptions = formControl.value;
    } else {
        selectedOptions = formControl;
    }
    if (selectedOptions) {
        if (selectedOptions.length > 1 && options) {
            displayText =
                selectedOptions.length + ' out of ' + options.length + ' selected';
        } else {
            if (options) {
                options.forEach((option) => {
                    if (option.value === selectedOptions[0]) {
                        displayText = option.label;
                    }
                });
            }
        }
    }
    return displayText;
}

function uuidv4() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
        const r = (Math.random() * 16) | 0,
            v = c === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
}

/**
 * Replace underscores with spaces and capitalize the first letter of each word.
 *
 * @param word
 */
function replaceUnderscoresWithSpacesAndCapitalizeEachWord(word: string) {
    return word
        .split(' ')
        .map((word) => word.replace('_', ' '))
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
}

/**
 * Counts the number of digits in a given number.
 * Uses a logarithmic approach for performance.
 * Reference: [Stack Overflow](https://stackoverflow.com/questions/14879691/get-number-of-digits-with-javascript/28203456#28203456)
 * @param n - number to count digits for
 * @param includeDecimals - whether to include decimals in the count
 * @returns number of digits in n
 */
function numberOfDigits(n: number, includeDecimals = false): number {
    if (includeDecimals) {
        n = Number(String(n).replace(/[^0-9]/g, ''));
    }
    return (Math.log10((n ^ (n >> 31)) - (n >> 31)) | 0) + 1;
}

export {
    parseDate,
    getObject,
    getObjectByKeyValue,
    setObject,
    transformModel,
    transform,
    updateCopyFields,
    calculateFields,
    getObjectDotPath,
    getFormComponentItemtext,
    getMultiSelectDisplayText,
    deepCopy,
    uuidv4,
    replaceUnderscoresWithSpacesAndCapitalizeEachWord,
    numberOfDigits,
    getFormControl,
    getDateWithDayOffset
};

/**
 * Simple object check.
 * @param item
 * @returns {boolean}
 */
export function isObject(item): boolean {
    return item && typeof item === 'object' && !Array.isArray(item);
}

/**
 * Deep merge two objects.
 * @param target
 * @param sources
 */
export function mergeDeep(target, ...sources) {
    if (!sources.length) return target;
    const source = sources.shift();
    if (isObject(target) && isObject(source)) {
        for (const key in source) {
            if (isObject(source[key])) {
                if (!target[key]) Object.assign(target, { [key]: {} });
                // don't deep merge if key is templateRef TODO: make this configurable
                if (key !== 'templateRef') {
                    mergeDeep(target[key], source[key]);
                }
            } else {
                Object.assign(target, { [key]: source[key] });
            }
        }
    }

    return mergeDeep(target, ...sources);
}

/**
 * Deep compare two objects.
 * @param object1
 * @param object2
 * @deprecated - This function has been deprecated and should not be used when developing new features.
 * Use objectDeepEquals instead, as it provides more reliable comparisons.
 */
export function deepEquals(object1: any, object2: any) {
    const keys1 = Object.keys(object1);
    const keys2 = Object.keys(object2);
    if (keys1.length !== keys2.length) {
        return false;
    }
    for (const key of keys1) {
        const val1 = object1[key];
        const val2 = object2[key];
        const areObjects = isObject(val1) && isObject(val2);
        if ((areObjects && !deepEquals(val1, val2)) || (!areObjects && val1 !== val2)) {
            return false;
        }
    }
    return true;
}

/**
 * Deep compare two objects (takes into consideration Date values in the objects.
 * @param object1
 * @param object2
 * @returns
 */
export function objectDeepEquals(
    object1: any,
    object2: any,
    options?: { ignoreNullish: boolean }
) {
    if (object1 === object2) return true;
    if (
        typeof object1 !== 'object' ||
        typeof object2 !== 'object' ||
        object1 === null ||
        object2 === null
    ) {
        return false;
    }

    let keys1 = Object.keys(object1);
    let keys2 = Object.keys(object2);
    if (options?.ignoreNullish) {
        keys1 = keys1.filter(
            (key) => object1[key] !== null && object1[key] !== undefined
        );
        keys2 = keys2.filter(
            (key) => object2[key] !== null && object2[key] !== undefined
        );
    }
    if (keys1.length !== keys2.length) {
        return false;
    }

    if (object1 instanceof Date && object2 instanceof Date) {
        return object1.getTime() === object2.getTime();
    }

    for (let key of keys1) {
        if (!keys2.includes(key)) {
            return false;
        }
        if (!objectDeepEquals(object1[key], object2[key])) {
            return false;
        }
    }

    return true;
}
